/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateSenderRequest } from '../models/create-sender-request';
import { GenericResponse } from '../models/generic-response';
import { GetSenderResponse } from '../models/get-sender-response';
import { GetVendorResponse } from '../models/get-vendor-response';
import { SenderDetailedResponse } from '../models/sender-detailed-response';
import { SenderResponse } from '../models/sender-response';
import { Status } from '../models/status';
import { UpdateSenderRequest } from '../models/update-sender-request';

/**
 * Sender
 */
@Injectable({ providedIn: 'root' })
export class SenderService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllSenders()` */
  static readonly GetAllSendersPath = '/api/v1/senders';

  /**
   * Get list of Senders.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllSenders()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllSenders$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - name
       */
      search?: string;

      /**
       * this adds a filter to the following field:
       * - user group
       */
      userGroupId?: Array<number>;

      /**
       * This adds a filter for the senders
       */
      status?: Status;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetSenderResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      SenderService.GetAllSendersPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('userGroupId', params.userGroupId, {});
      rb.query('status', params.status, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetSenderResponse>;
        })
      );
  }

  /**
   * Get list of Senders.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllSenders$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllSenders(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - name
       */
      search?: string;

      /**
       * this adds a filter to the following field:
       * - user group
       */
      userGroupId?: Array<number>;

      /**
       * This adds a filter for the senders
       */
      status?: Status;
    },
    context?: HttpContext
  ): Observable<GetSenderResponse> {
    return this.getAllSenders$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetSenderResponse>): GetSenderResponse => r.body
      )
    );
  }

  /** Path part for operation `createSender()` */
  static readonly CreateSenderPath = '/api/v1/senders';

  /**
   * create Sender.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createSender()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createSender$Response(
    params: {
      body: CreateSenderRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<SenderResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      SenderService.CreateSenderPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<SenderResponse>;
        })
      );
  }

  /**
   * create Sender.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createSender$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createSender(
    params: {
      body: CreateSenderRequest;
    },
    context?: HttpContext
  ): Observable<SenderResponse> {
    return this.createSender$Response(params, context).pipe(
      map((r: StrictHttpResponse<SenderResponse>): SenderResponse => r.body)
    );
  }

  /** Path part for operation `getSenderById()` */
  static readonly GetSenderByIdPath = '/api/v1/senders/{id}';

  /**
   * fetch Sender details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSenderById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSenderById$Response(
    params: {
      /**
       * The sender id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<SenderDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      SenderService.GetSenderByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<SenderDetailedResponse>;
        })
      );
  }

  /**
   * fetch Sender details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getSenderById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSenderById(
    params: {
      /**
       * The sender id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<SenderDetailedResponse> {
    return this.getSenderById$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<SenderDetailedResponse>
        ): SenderDetailedResponse => r.body
      )
    );
  }

  /** Path part for operation `deleteSenderById()` */
  static readonly DeleteSenderByIdPath = '/api/v1/senders/{id}';

  /**
   * Delete Sender by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteSenderById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteSenderById$Response(
    params: {
      /**
       * The Sender id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      SenderService.DeleteSenderByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete Sender by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteSenderById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteSenderById(
    params: {
      /**
       * The Sender id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteSenderById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateSender()` */
  static readonly UpdateSenderPath = '/api/v1/senders/{id}';

  /**
   * update Sender.
   *
   * update Sender
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateSender()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSender$Response(
    params: {
      /**
       * The Sender id that needs to be updated
       */
      id: number;
      body: UpdateSenderRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<SenderResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      SenderService.UpdateSenderPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<SenderResponse>;
        })
      );
  }

  /**
   * update Sender.
   *
   * update Sender
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateSender$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSender(
    params: {
      /**
       * The Sender id that needs to be updated
       */
      id: number;
      body: UpdateSenderRequest;
    },
    context?: HttpContext
  ): Observable<SenderResponse> {
    return this.updateSender$Response(params, context).pipe(
      map((r: StrictHttpResponse<SenderResponse>): SenderResponse => r.body)
    );
  }

  /** Path part for operation `getAllVendors()` */
  static readonly GetAllVendorsPath = '/api/v1/vendors';

  /**
   * Get list of vendors.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllVendors()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllVendors$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - name
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetVendorResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      SenderService.GetAllVendorsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetVendorResponse>;
        })
      );
  }

  /**
   * Get list of vendors.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllVendors$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllVendors(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - name
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetVendorResponse> {
    return this.getAllVendors$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetVendorResponse>): GetVendorResponse => r.body
      )
    );
  }
}
